import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '../Button';
import { useT } from '../../i18n';
import { lockBodyScroll } from '../../lib/scrollLock';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Width preset. Defaults to 'md' (480px). */
  size?: 'sm' | 'md' | 'lg';
  /** Hide the close (X) button. */
  hideClose?: boolean;
  /** Footer slot — typically primary + cancel buttons. */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Centered modal. Reserved for confirmations and short forms (per §4 of
 * ui-system.md). For record edits + multi-step flows prefer Drawer.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  hideClose,
  footer,
  children,
}: ModalProps) {
  const { t } = useT();
  // Lock body scroll while open. Close on Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        // Click-outside closes only when the down-press was on the overlay
        // (so a drag-select that ends outside doesn't accidentally dismiss).
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={clsx(styles.dialog, styles[`size_${size}`])}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id="modal-title" className={styles.title}>{title}</h2>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          {!hideClose && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onClose}
              aria-label={t('components.modal.close')}
              leftIcon={<X size={16} />}
            />
          )}
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
